import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { LANGS, dirFor, t as translate } from "./i18n";

/* Language context — lang code + direction + tr() helper.
   Persisted to localStorage so the switcher choice survives reloads
   (this is a UI preference, not assessment state — assessment stays in memory). */
const LangCtx = createContext(null);
const STORAGE_KEY = "onehealth_lang";

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && LANGS.some((l) => l.code === saved)) return saved;
    } catch (_) {}
    return "en";
  });

  const dir = dirFor(lang);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", dir);
  }, [lang, dir]);

  const setLang = useCallback((code) => {
    setLangState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) {}
  }, []);

  const tr = useCallback((key, vars) => translate(lang, key, vars), [lang]);

  return <LangCtx.Provider value={{ lang, dir, setLang, tr }}>{children}</LangCtx.Provider>;
}

export function useLang() {
  const ctx = useContext(LangCtx);
  if (!ctx) throw new Error("useLang must be used inside LangProvider");
  return ctx;
}

/* Globe icon — denotes language selection. */
function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="oh-globe">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.6 2.4 4 5.6 4 9s-1.4 6.6-4 9c-2.6-2.4-4-5.6-4-9s1.4-6.6 4-9z" />
    </svg>
  );
}

/* Compact language switcher — a globe icon that opens a small language menu.
   Lives in the top device bezel. */
export function LanguageSwitcher() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find((l) => l.code === lang);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="oh-lang" ref={ref}>
      <button
        type="button"
        className="oh-lang-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        title="Language"
      >
        <GlobeIcon />
        <span className="oh-lang-cur">{current ? current.label : lang.toUpperCase()}</span>
      </button>
      {open && (
        <div className="oh-lang-menu" role="listbox" aria-label="Language">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              role="option"
              aria-selected={l.code === lang}
              className={"oh-lang-opt" + (l.code === lang ? " on" : "")}
              onClick={() => { setLang(l.code); setOpen(false); }}
            >
              <span className="oh-lang-opt-label">{l.label}</span>
              <span className="oh-lang-opt-name">{l.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
